import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionDataService } from '../service/session-data.service';
import { SpinnerService } from '../../spinner-component/spinner.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-summary-details-page',
  templateUrl: './summary-details-page.component.html',
  styleUrls: ['./summary-details-page.component.css'],
  standalone: false
})
export class SummaryDetailsPageComponent implements OnInit {
  timeRange: string = 'day';
  startDate: any;
  endDate: any;
  potentialId: string = '';
  accountId: string = '';
  summaryData: any = null;
  siteName: string = '';
  
  // For account/potentialId selection
  selectedViewType: string = 'account'; // 'account' or 'potentialId'
  availablePotentialIds: any[] = [];
  selectedPotentialId: string = '';
  accountName: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sessionDataService: SessionDataService,
    private spinner: SpinnerService,
    private toaster: ToastrService
  ) {
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id) {
        // Determine if it's accountId or potentialId by checking sites
        this.accountId = id;
        this.potentialId = id;
        this.loadAccountDetails();
        // Don't call applyFilter here - it will be called after fetchAccountSites completes
      }
    });
    
    // Check for siteName in query params (if passed from navigation)
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['siteName']) {
        this.siteName = queryParams['siteName'];
      }
    });
  }

  loadAccountDetails(): void {
    // Fetch all sites to determine accountId and get all potentialIds
    if (this.accountId || this.potentialId) {
      const id = this.accountId || this.potentialId;
      this.fetchAccountSites(id);
    }
  }

  fetchAccountSites(id: string): void {
    // Fetch all sites to find accountId and all potentialIds under it
    const url = `${environment.workSpaceAPI}/user/getSites?page=0&size=1000`;
    this.sessionDataService.searchSites(url).subscribe({
      next: (response: any) => {
        if (response && response.data && response.data.response) {
          let dataArray: any[] = [];
          Object.keys(response.data.response).forEach((key) => {
            dataArray = dataArray.concat(response.data.response[key]);
          });
          
          // Find the site with matching potentialId or accountId
          const matchingSite = dataArray.find(s => 
            s.potentialId == id || s.accountId == id || 
            String(s.potentialId) === String(id) || String(s.accountId) === String(id)
          );
          
          if (matchingSite) {
            // Determine if the ID is accountId or potentialId
            if (String(matchingSite.accountId) === String(id)) {
              // It's an accountId - get all potentialIds under this account
              this.accountId = matchingSite.accountId;
              this.accountName = matchingSite.accountName || '';
              this.siteName = this.accountName || 'Account Summary';
              
              // Get all potentialIds under this accountId
              const sitesUnderAccount = dataArray.filter(s => 
                String(s.accountId) === String(this.accountId)
              );
              
              this.availablePotentialIds = sitesUnderAccount.map(site => ({
                potentialId: site.potentialId,
                siteName: site.siteName,
                accountId: site.accountId
              }));
              
              // Default to account view (aggregated)
              this.selectedViewType = 'account';
              this.selectedPotentialId = '';
            } else {
              // It's a potentialId - find the accountId
              this.potentialId = matchingSite.potentialId;
              this.accountId = matchingSite.accountId;
              this.accountName = matchingSite.accountName || '';
              this.siteName = matchingSite.siteName || '';
              
              // Get all potentialIds under this accountId
              const sitesUnderAccount = dataArray.filter(s => 
                String(s.accountId) === String(this.accountId)
              );
              
              this.availablePotentialIds = sitesUnderAccount.map(site => ({
                potentialId: site.potentialId,
                siteName: site.siteName,
                accountId: site.accountId
              }));
              
              // Default to account view (aggregated), but allow switching to this potentialId
              this.selectedViewType = 'account';
              this.selectedPotentialId = '';
            }
            
            // After fetching sites and populating availablePotentialIds, apply filter
            if (this.availablePotentialIds.length > 0) {
              this.applyFilter();
            }
          } else {
            // No matching site found - still try to apply filter with the ID we have
            if (this.accountId) {
              this.applyFilter();
            }
          }
        }
      },
      error: (error) => {
        console.error('Error fetching account sites:', error);
        // Even if there's an error, try to apply filter with the ID we have
        if (this.accountId) {
          this.applyFilter();
        }
      }
    });
  }

  onTimeRangeChange(): void {
    this.applyFilter();
  }

  onCustomDateChange(): void {
    if (this.startDate && this.endDate) {
      this.applyFilter();
    }
  }

  applyFilter(): void {
    let fromDate: string;
    let toDate: string;
    const currentDate = new Date();
    
    if (this.timeRange === 'day') {
      // Day = current date
      fromDate = this.formatDate(currentDate);
      toDate = this.formatDate(currentDate);
    } else if (this.timeRange === 'week') {
      // Week = last 7 days
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - 7);
      fromDate = this.formatDate(startOfWeek);
      toDate = this.formatDate(currentDate);
    } else if (this.timeRange === 'month') {
      // Month = last 30 days (not calendar month, but 30 days back)
      const oneMonthAgo = new Date(currentDate);
      oneMonthAgo.setDate(currentDate.getDate() - 30);
      fromDate = this.formatDate(oneMonthAgo);
      toDate = this.formatDate(currentDate);
    } else if (this.timeRange === 'custom' && this.startDate && this.endDate) {
      fromDate = this.customFormatDate(this.startDate);
      toDate = this.customFormatDate(this.endDate);
    } else {
      // Default to day
      fromDate = this.formatDate(currentDate);
      toDate = this.formatDate(currentDate);
    }
    
    this.fetchSummaryData(fromDate, toDate);
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  customFormatDate(momentDate: any): string {
    if (momentDate && momentDate.format) {
      return momentDate.format('YYYY-MM-DD');
    }
    // Fallback if not moment date
    const date = new Date(momentDate);
    return this.formatDate(date);
  }

  onViewTypeChange(): void {
    if (this.selectedViewType === 'potentialId' && this.availablePotentialIds.length > 0 && !this.selectedPotentialId) {
      // Auto-select first potentialId if none selected
      this.selectedPotentialId = this.availablePotentialIds[0].potentialId;
      this.onPotentialIdChange();
    } else if (this.selectedViewType === 'account') {
      this.selectedPotentialId = '';
      this.siteName = this.accountName || 'Account Summary';
      this.applyFilter();
    } else {
      this.applyFilter();
    }
  }

  onPotentialIdChange(): void {
    if (this.selectedViewType === 'potentialId' && this.selectedPotentialId) {
      const selectedSite = this.availablePotentialIds.find(p => 
        String(p.potentialId) === String(this.selectedPotentialId)
      );
      if (selectedSite) {
        this.siteName = selectedSite.siteName || '';
        this.applyFilter();
      }
    } else if (this.selectedViewType === 'account') {
      this.siteName = this.accountName || 'Account Summary';
    }
    this.applyFilter();
  }

  fetchSummaryData(fromDate: string, toDate: string): void {
    // Always use account aggregated data
    if (this.accountId && this.availablePotentialIds.length > 0) {
      this.fetchAccountAggregatedData(fromDate, toDate);
    } else if (this.accountId && this.availablePotentialIds.length === 0) {
      // If we have accountId but no potentialIds yet, wait a bit or use accountId directly
      // Don't show error - just wait for data to load
      console.log('Waiting for potentialIds to be loaded...');
    } else {
      // Only show error if we truly don't have an accountId
      console.error('Account ID not available');
    }
  }

  fetchAccountAggregatedData(fromDate: string, toDate: string): void {
    if (!this.accountId || this.availablePotentialIds.length === 0) {
      console.warn('Account ID or Potential IDs not available yet');
      return;
    }

    this.spinner.activate();
    
    // Fetch data for all potentialIds and aggregate
    const requests = this.availablePotentialIds.map(p => 
      this.sessionDataService.getNewsletterData(p.potentialId, fromDate, toDate)
    );

    // Use forkJoin to fetch all in parallel, then aggregate
    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        this.spinner.deactivate();
        
        // Aggregate all responses
        const aggregatedData = {
          humanReviewCount: 0,
          activityCount: 0,
          threatCount: 0,
          totalCrimeCount: 0,
          pcScCalled: 0,
          policeNotified: 0,
          alarmCount: 0,
          arrestIncidents: [] as any[],
          deterredIncidents: [] as any[],
          interventionIncidents: [] as any[]
        };

        responses.forEach((response: any) => {
          if (response && response.success && response.data) {
            aggregatedData.humanReviewCount += (response.data.humanReviewCount || 0);
            aggregatedData.activityCount += (response.data.activityCount || 0);
            aggregatedData.threatCount += (response.data.threatCount || 0);
            aggregatedData.totalCrimeCount += (response.data.totalCrimeCount || 0);
            aggregatedData.pcScCalled += (response.data.pcScCalled || 0);
            aggregatedData.policeNotified += (response.data.policeNotified || 0);
            aggregatedData.alarmCount += (response.data.alarmCount || 0);
            
            // Aggregate incident arrays
            if (response.data.arrestIncidents && Array.isArray(response.data.arrestIncidents)) {
              aggregatedData.arrestIncidents = aggregatedData.arrestIncidents.concat(response.data.arrestIncidents);
            }
            if (response.data.deterredIncidents && Array.isArray(response.data.deterredIncidents)) {
              aggregatedData.deterredIncidents = aggregatedData.deterredIncidents.concat(response.data.deterredIncidents);
            }
            if (response.data.interventionIncidents && Array.isArray(response.data.interventionIncidents)) {
              aggregatedData.interventionIncidents = aggregatedData.interventionIncidents.concat(response.data.interventionIncidents);
            }
          }
        });

        this.summaryData = aggregatedData;
      },
      error: (error) => {
        this.spinner.deactivate();
        console.error('Error fetching aggregated summary data:', error);
        this.toaster.error('Failed to fetch aggregated summary data');
        this.summaryData = null;
      }
    });
  }

  fetchPotentialIdData(potentialId: string, fromDate: string, toDate: string): void {
    this.spinner.activate();
    
    this.sessionDataService.getNewsletterData(potentialId, fromDate, toDate).subscribe({
      next: (response: any) => {
        this.spinner.deactivate();
        if (response && response.success && response.data) {
          // Ensure incident arrays exist
          this.summaryData = {
            ...response.data,
            arrestIncidents: response.data.arrestIncidents || [],
            deterredIncidents: response.data.deterredIncidents || [],
            interventionIncidents: response.data.interventionIncidents || []
          };
        } else {
          this.toaster.warning('No data available for the selected date range');
          this.summaryData = null;
        }
      },
      error: (error) => {
        this.spinner.deactivate();
        console.error('Error fetching summary data:', error);
        this.toaster.error('Failed to fetch summary data');
        this.summaryData = null;
      }
    });
  }

  getTotalCount(): number {
    if (!this.summaryData) return 0;
    return (this.summaryData.humanReviewCount || 0) +
           (this.summaryData.activityCount || 0) +
           (this.summaryData.threatCount || 0) +
           (this.summaryData.totalCrimeCount || 0) +
           (this.summaryData.pcScCalled || 0) +
           (this.summaryData.policeNotified || 0) +
           (this.summaryData.alarmCount || 0);
  }

  getCurrentMonthYear(): string {
    const currentDate = new Date();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  }

  getDateRangeText(): string {
    const currentDate = new Date();
    let fromDate: Date;
    let toDate: Date = currentDate;
    
    if (this.timeRange === 'day') {
      fromDate = currentDate;
      toDate = currentDate;
    } else if (this.timeRange === 'week') {
      fromDate = new Date(currentDate);
      fromDate.setDate(currentDate.getDate() - 7);
    } else if (this.timeRange === 'month') {
      fromDate = new Date(currentDate);
      fromDate.setDate(currentDate.getDate() - 30);
    } else if (this.timeRange === 'custom' && this.startDate && this.endDate) {
      fromDate = this.startDate && this.startDate.toDate ? this.startDate.toDate() : new Date(this.startDate);
      toDate = this.endDate && this.endDate.toDate ? this.endDate.toDate() : new Date(this.endDate);
    } else {
      fromDate = currentDate;
      toDate = currentDate;
    }
    
    const formatDate = (date: Date): string => {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    };
    
    if (fromDate.getTime() === toDate.getTime()) {
      return formatDate(fromDate);
    } else {
      return `${formatDate(fromDate)} to ${formatDate(toDate)}`;
    }
  }

  onImageError(event: any): void {
    if (event && event.target) {
      event.target.style.display = 'none';
    }
  }

  goBack(): void {
    this.router.navigate(['/cp/user-management']);
  }
}

